<template>
  <component :is="shellIs" v-bind="shellBind">
    <el-alert
      v-if="context && context.blockReason"
      type="warning"
      :closable="false"
      show-icon
      style="margin-bottom:16px;"
      :title="context.blockReason"
    />
    <el-form :label-width="embedded ? '108px' : '120px'" size="medium" :class="['form-page-form', { 'form-page-form--embed': embedded }]">
      <el-form-item v-if="!quoteLocked" :label="$t('contractFromQuotePage.selectQuote')" required>
        <el-select
          v-model="selectedQuoteId"
          filterable
          clearable
          :placeholder="$t('contractFromQuotePage.quotePh')"
          :loading="quoteListLoading"
          style="width:100%;"
          @change="onQuoteChange"
        >
          <el-option
            v-for="q in quoteOptions"
            :key="q.id"
            :label="formatQuoteOption(q)"
            :value="q.id"
          />
        </el-select>
      </el-form-item>

      <template v-if="context && context.exists && context.quote">
        <div class="quote-ctx-panel">
          <div class="quote-ctx-panel__header">
            <i class="el-icon-s-order quote-ctx-panel__icon" aria-hidden="true" />
            <span>{{ $t('contractFromQuotePage.quoteSummary') }}</span>
          </div>
          <el-descriptions :column="1" border size="small" class="quote-ctx-desc">
            <el-descriptions-item :label="$t('contractFromQuotePage.quoteNo')">
              <span class="quote-ctx-val quote-ctx-val--mono">{{ context.quote.quoteNo || '—' }}</span>
            </el-descriptions-item>
            <el-descriptions-item :label="$t('contractFromQuotePage.lead')">
              <span class="quote-ctx-val">{{ context.leadNo || '—' }} · {{ context.leadDisplayName || '—' }}</span>
            </el-descriptions-item>
            <el-descriptions-item :label="$t('contractFromQuotePage.customer')">
              {{ context.customerDisplayName || '—' }}
            </el-descriptions-item>
            <el-descriptions-item :label="$t('contractFromQuotePage.quoteAmount')">
              <span class="quote-ctx-val quote-ctx-val--amt">{{ formatUsd(context.quote.totalAmount) }} USD</span>
            </el-descriptions-item>
            <el-descriptions-item :label="$t('contractFromQuotePage.status')">
              {{ labelQuotePrimary(context.quote) }}
            </el-descriptions-item>
            <el-descriptions-item v-if="context.existingContractId" :label="$t('contractFromQuotePage.existingContract')">
              <el-link type="primary" @click.native.prevent="openContractDetail(context.existingContractId)">
                {{ $t('contractFromQuotePage.viewContract') }}{{ context.existingContractId }}
              </el-link>
            </el-descriptions-item>
          </el-descriptions>
        </div>
      </template>

      <el-form-item :label="$t('contractFromQuotePage.scheduleBlockLabel')" class="contract-from-quote__schedule-item">
        <div class="contract-from-quote__schedule-shell">
          <div class="contract-from-quote__schedule-row">
            <div class="contract-from-quote__schedule-cell">
              <el-date-picker
                v-model="form.startDate"
                type="date"
                value-format="yyyy-MM-dd"
                :placeholder="$t('contractFromQuotePage.startPh')"
                class="contract-from-quote__schedule-picker contract-from-quote__schedule-picker--fill"
              />
            </div>
            <div class="contract-from-quote__schedule-cell contract-from-quote__schedule-cell--mode">
              <el-radio-group v-model="termMode" size="small" class="contract-from-quote__term-mode contract-from-quote__term-mode--row">
                <el-radio-button label="months">{{ $t('contractFromQuotePage.termByMonths') }}</el-radio-button>
                <el-radio-button label="endDate">{{ $t('contractFromQuotePage.termByEndDate') }}</el-radio-button>
              </el-radio-group>
            </div>
            <div
              class="contract-from-quote__schedule-cell contract-from-quote__schedule-term"
              :class="{ 'contract-from-quote__schedule-term--end': termMode === 'endDate' }"
            >
              <el-input-number
                v-if="termMode === 'months'"
                v-model="form.contractTermMonths"
                :min="1"
                :max="120"
                :step="1"
                controls-position="right"
                class="contract-from-quote__term-field"
              />
              <el-date-picker
                v-else
                v-model="form.endDate"
                type="date"
                value-format="yyyy-MM-dd"
                :placeholder="$t('contractFromQuotePage.endPh')"
                class="contract-from-quote__schedule-picker contract-from-quote__schedule-picker--end"
                clearable
              />
            </div>
          </div>
        </div>
      </el-form-item>

      <el-form-item :label="$t('contractFromQuotePage.template')">
        <el-select
          v-model="form.templateId"
          clearable
          filterable
          :placeholder="$t('contractFromQuotePage.templatePhLong')"
          style="width:100%;"
        >
          <el-option
            v-for="t in enabledTemplates"
            :key="t.id"
            :label="(t.templateName || '').trim() || $t('common.unnamedTemplate')"
            :value="t.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item :label="$t('contractFromQuotePage.contractTitle')">
        <el-input v-model="form.contractTitle" :placeholder="$t('contractFromQuotePage.titlePh')" clearable />
      </el-form-item>

      <el-form-item v-if="!embedded" class="form-page-form__actions">
        <el-button type="primary" :loading="submitting" :disabled="!canSubmit" @click="submit">{{ $t('contractFromQuotePage.submitOpenDetail') }}</el-button>
        <el-button @click="$router.push('/contract/list')">{{ $t('common.backToList') }}</el-button>
      </el-form-item>
    </el-form>
  </component>
</template>

<script>
import { createContractFromQuote, getContractFromQuoteContext } from '@/api/contract'
import { getTemplatePage } from '@/api/contractTemplate'
import { getQuoteDetail, getQuotePage } from '@/api/quote'
import { labelQuotePrimary } from '@/utils/crmLabels'
import { formatUsd } from '@/utils/formatUsd'
import { contractDetailLocation } from '@/utils/contractDetailNav'
import FormPageShell from '@/components/FormPageShell.vue'

function localDateYmd() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export default {
  name: 'ContractFromQuote',
  components: { FormPageShell },
  props: {
    embedded: { type: Boolean, default: false },
    /** 弹窗内从指定报价创建时锁定报价，不显示报价下拉 */
    presetQuoteId: { type: [Number, String], default: null }
  },
  data() {
    return {
      templates: [],
      quoteOptions: [],
      quoteListLoading: false,
      selectedQuoteId: '',
      context: null,
      submitting: false,
      /** months：按月数；endDate：仅结束日 */
      termMode: 'months',
      form: {
        templateId: '',
        contractTitle: '',
        startDate: localDateYmd(),
        contractTermMonths: 12,
        endDate: ''
      }
    }
  },
  watch: {
    termMode(val) {
      if (val === 'months') {
        this.form.endDate = ''
      }
    }
  },
  computed: {
    quoteLocked() {
      return this.embedded && this.presetQuoteId != null && this.presetQuoteId !== ''
    },
    shellIs() {
      return this.embedded ? 'div' : 'FormPageShell'
    },
    shellBind() {
      if (this.embedded) {
        return { class: 'contract-from-quote__embed' }
      }
      return {
        isEdit: false,
        title: this.$t('contractFromQuotePage.title'),
        subtitle: this.$t('contractFromQuotePage.subtitle'),
        icon: 'el-icon-document-copy',
        theme: 'warning'
      }
    },
    enabledTemplates() {
      return (this.templates || []).filter(t => t.templateStatusCode == null || Number(t.templateStatusCode) === 1)
    },
    canSubmit() {
      if (!this.selectedQuoteId) return false
      if (!this.context || !this.context.canCreateContract) return false
      return true
    }
  },
  async created() {
    await this.loadTemplates()
    if (this.embedded) {
      if (this.quoteLocked) {
        await this.bootstrapLockedQuote()
      } else {
        await this.loadEligibleQuotes()
      }
      return
    }
    await this.loadEligibleQuotes()
    const qid = this.$route.query.quoteId
    if (qid) {
      const n = Number(qid)
      this.selectedQuoteId = n
      const inList = this.quoteOptions.some(q => Number(q.id) === n)
      if (!inList) {
        try {
          const r = await getQuoteDetail(n)
          const d = r.data !== undefined ? r.data : r
          const q = d.quote
          if (q) this.quoteOptions = [q, ...this.quoteOptions.filter(x => Number(x.id) !== Number(q.id))]
        } catch (e) {
          /* ignore */
        }
      }
      await this.loadContext(n)
    }
  },
  methods: {
    openContractDetail(contractId) {
      this.$router.push(contractDetailLocation(contractId, this.$route.fullPath))
    },
    formatUsd,
    labelQuotePrimary,
    async bootstrapLockedQuote() {
      const n = Number(this.presetQuoteId)
      if (!n) return
      this.selectedQuoteId = n
      try {
        const r = await getQuoteDetail(n)
        const d = r.data !== undefined ? r.data : r
        const q = d.quote
        if (q) this.quoteOptions = [q]
      } catch (e) {
        this.quoteOptions = []
      }
      await this.loadContext(n)
    },
    formatQuoteOption(q) {
      if (!q) return ''
      const no = (q.quoteNo || '').trim()
      const st = labelQuotePrimary(q)
      return no ? `${no} · ${st}` : this.$t('contractFromQuotePage.quoteNum', { id: q.id })
    },
    async loadTemplates() {
      try {
        const r = await getTemplatePage({ pageNo: 1, pageSize: 200 })
        const d = r.data || r
        this.templates = d.records || []
      } catch (e) {
        this.templates = []
      }
    },
    async loadEligibleQuotes() {
      this.quoteListLoading = true
      try {
        const r = await getQuotePage({ pageNo: 1, pageSize: 500, quoteStatusCode: 5 })
        const d = r.data || r
        this.quoteOptions = d.records || []
      } catch (e) {
        this.quoteOptions = []
        this.$message.error((e && e.message) || this.$t('contractFromQuotePage.loadQuotesFailed'))
      } finally {
        this.quoteListLoading = false
      }
    },
    async onQuoteChange(id) {
      this.context = null
      if (!id) return
      await this.loadContext(Number(id))
    },
    async loadContext(quoteId) {
      try {
        const r = await getContractFromQuoteContext(quoteId)
        const ctx = r.data !== undefined ? r.data : r
        this.context = ctx
        if (ctx && ctx.quote && !this.form.contractTitle) {
          const on = ctx.leadDisplayName || ''
          const qn = ctx.quote.quoteNo || ''
          this.form.contractTitle = (on ? on.trim() : '') + (on && qn ? ' · ' : '') + (qn ? qn.trim() : '')
        }
      } catch (e) {
        this.context = { exists: false, blockReason: (e && e.message) || this.$t('contractFromQuotePage.loadContextFailed') }
      }
    },
    async submit() {
      if (!this.canSubmit) {
        this.$message.warning(this.$t('contractFromQuotePage.selectQuoteWarning'))
        return
      }
      if (this.termMode === 'endDate' && !this.form.endDate) {
        this.$message.warning(this.$t('contractFromQuotePage.needEndDate'))
        return
      }
      this.submitting = true
      this.$emit('saving', true)
      try {
        const payload = {
          quoteId: Number(this.selectedQuoteId),
          templateId: this.form.templateId ? Number(this.form.templateId) : null,
          contractTitle: this.form.contractTitle || undefined,
          startDate: this.form.startDate || undefined,
          endDate: this.form.endDate || undefined,
          contractTermMonths: this.form.endDate ? undefined : Number(this.form.contractTermMonths) || 12
        }
        const r = await createContractFromQuote(payload)
        const id = r.data !== undefined ? r.data : r
        this.$message.success(this.$t('common.created'))
        if (this.embedded) {
          this.$emit('saved', { id })
        } else {
          this.$router.replace(contractDetailLocation(id, this.$route.fullPath))
        }
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('contractFromQuotePage.failed'))
      } finally {
        this.submitting = false
        this.$emit('saving', false)
      }
    }
  }
}
</script>

<style scoped>
.form-page-form {
  max-width: 100%;
}

.form-page-form--embed {
  max-width: none;
}

.contract-from-quote__embed {
  min-width: 0;
}

.form-page-form__actions {
  margin-top: 8px;
  margin-bottom: 0;
}

.quote-ctx-panel {
  margin-bottom: 20px;
  border-radius: 12px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  box-shadow: var(--shadow-card);
  overflow: hidden;
}

.quote-ctx-panel__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-border-soft);
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text);
  letter-spacing: 0.02em;
}

.quote-ctx-panel__icon {
  font-size: 16px;
  color: #fbbf24;
}

.quote-ctx-desc {
  margin: 0;
}

.quote-ctx-desc >>> .el-descriptions__body {
  background: transparent;
}

.quote-ctx-desc >>> .el-descriptions .is-bordered .el-descriptions-item__cell {
  padding: 10px 14px;
  font-size: 13px;
}

.quote-ctx-val--mono {
  font-family: 'SF Mono', ui-monospace, Consolas, monospace;
  letter-spacing: -0.02em;
}

.quote-ctx-val--amt {
  font-weight: 600;
  color: var(--color-text);
}

.form-page-form .contract-from-quote__schedule-item.el-form-item {
  align-items: center !important;
}

.contract-from-quote__schedule-item >>> .el-form-item__content {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
}

/* 三块控件包在同一视觉块里，等分列宽，避免「两头小、中间一条」的松散感 */
.contract-from-quote__schedule-shell {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: var(--modal-radius-input, 8px);
  border: 1px solid var(--modal-input-border, rgba(15, 23, 42, 0.12));
  background: var(--color-surface-soft);
}

.contract-from-quote__schedule-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 1fr);
  gap: 10px;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
}

.contract-from-quote__schedule-cell {
  min-width: 0;
  display: flex;
  align-items: center;
}

.contract-from-quote__schedule-cell--mode {
  align-self: center;
}

.contract-from-quote__schedule-picker {
  max-width: 100%;
}

.contract-from-quote__schedule-picker--fill,
.contract-from-quote__schedule-picker--end {
  width: 100% !important;
}

.contract-from-quote__schedule-term {
  min-width: 0;
}

.contract-from-quote__term-mode {
  display: flex;
  flex-wrap: nowrap;
  border-radius: var(--modal-radius-input, 8px);
  overflow: hidden;
  border: 1px solid var(--modal-input-border, rgba(255, 255, 255, 0.065));
  background: var(--modal-input-bg, rgba(255, 255, 255, 0.03));
}

.contract-from-quote__term-mode--row {
  width: 100%;
  min-width: 0;
}

.contract-from-quote__term-mode >>> .el-radio-button {
  flex: 1 1 0;
  min-width: 0;
}

.contract-from-quote__term-mode >>> .el-radio-button__inner {
  width: 100%;
  padding: 9px 12px;
  text-align: center;
  color: var(--dark-text-sub, #8b95a1);
  background: transparent;
  border: 0;
  border-radius: 0;
  border-left: 1px solid var(--modal-input-border, rgba(255, 255, 255, 0.065));
  box-shadow: none;
  transition: color 0.15s ease, background-color 0.15s ease;
  font-weight: 500;
}

.contract-from-quote__term-mode >>> .el-radio-button:first-child .el-radio-button__inner {
  border-left: 0;
}

/* 与弹窗主按钮同色（--modal-primary / #2563eb），避免另一套「发灰绿」 */
.contract-from-quote__term-mode >>> .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  color: #fff !important;
  background: var(--modal-primary, #2563eb) !important;
}

.contract-from-quote__term-mode >>> .el-radio-button__inner:hover {
  color: var(--color-text);
  background: var(--layout-hover-bg);
}

.contract-from-quote__term-mode >>> .el-radio-button__orig-radio:checked + .el-radio-button__inner:hover {
  color: #fff !important;
  background: var(--modal-primary-hover, #3b82f6) !important;
}

.contract-from-quote__term-field {
  width: 100%;
  max-width: 100%;
}

.contract-from-quote__schedule-term--end {
  min-width: 0;
}

@media (max-width: 520px) {
  .contract-from-quote__schedule-row {
    grid-template-columns: 1fr;
  }
}
</style>
